import { useState, useEffect, useMemo } from 'react';
import { discountService } from '../services/api';
import type { FuelDiscount, FuelDiscountGroup } from '../types';
import { useAuth } from '../contexts/AuthContext';

export function useFuelDiscounts() {
  const { operator } = useAuth();
  const [fuelGroups, setFuelGroups] = useState<FuelDiscountGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFuelDiscounts = async () => {
    if (!operator) return;
    setLoading(true);
    setError(null);

    try {
      const data = await discountService.getOperatorFuelDiscounts(operator.id);
      const validatedData = Array.isArray(data) ? data : [];
      setFuelGroups(validatedData);
    } catch (err) {
      console.error('Error fetching fuel discounts:', err);
      setError('Error al cargar los cargues de combustible');
      setFuelGroups([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFuelDiscounts();
  }, [operator]);

  const flatDiscounts = useMemo<FuelDiscount[]>(
    () => (Array.isArray(fuelGroups) ? fuelGroups.flatMap(g => g.fuelDiscounts) : []),
    [fuelGroups]
  );

  const stats = useMemo(() => {
    const totalLoads = flatDiscounts.length;
    const totalGallons = flatDiscounts.reduce((acc, d) => acc + (Number(d.gallons) || 0), 0);
    return { totalLoads, totalGallons };
  }, [flatDiscounts]);

  return {
    fuelGroups,
    flatDiscounts,
    stats,
    loading,
    error,
    refetch: fetchFuelDiscounts,
  };
}
